import { ICoreContext } from '@msdyn365-commerce/core';
import { AsyncResult } from '@msdyn365-commerce/retail-proxy';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { IWineClubMemberData } from '../../../dataActions/DataServiceEntities.g';
import { BusLoader, LoadBus } from '../../../Utilities/event-bus';
import { createEditMembershipInput, getEditMembershipAction, IMembershipEditMembershipInput } from '../actions/membership-edit.action';
import membershipStateAction, { createMembershipStateInput, IMembershipStateInput } from '../actions/membership-state.action';
import wineShipmentStateAction, { createWineShipmentStateInput } from '../actions/wine-shipment-state.action';
import { IWineClubManagementProps } from '../wine-club-management.props.autogenerated';
import { IWineClubActionProps, WineClubAction } from './wine-club-action';
import { IWineClubEditFormProps, WineClubEditForm, wineClubEditFormData } from './wine-club-edit';
interface IWineClubMembershipProps {
    membership: IWineClubMemberData;
    managementProps: IWineClubManagementProps<{}>;
    context: ICoreContext;
    isEditingClub: boolean;
    updateEditingClub(state: boolean): void;
}

export type clubStatusTableType = {
    ACTIVE: string;
    INACTIVE: string;
    CLOSED: string;
    PENDING: string;
};

/**
 * renders a singular wine club membership and allows the modifications
 * of various pieces of said wine club membership
 */
@LoadBus('wine-club-management')
@observer
export class WineClubMembership extends React.Component<IWineClubMembershipProps> {

    private bus: BusLoader | undefined;
    @observable private _currentModal: JSX.Element | undefined;
    @observable private _dropdownToggle: boolean = false;
    @observable private _currentModalProps: IWineClubActionProps | undefined;
    @observable private _editModalProps: IWineClubEditFormProps | undefined;
    @observable private _successMessage: string | undefined;

    private _keyRefresher: number = 0;
    private _clubStatusTable: clubStatusTableType = {
        ACTIVE: 'Active',
        INACTIVE: 'Inactive',
        CLOSED: 'Closed',
        PENDING: 'Pending'
    };

    private readonly _errorCodeMap: { [code: string]: string } = {
        INVALID_CUSTOMER: this.props.managementProps.resources.wineClubManagement__errInvalidCustomer,
        INVALID_WINECLUBID: this.props.managementProps.resources.wineClubManagement__errInvalidWineClub,
        PROCESS_ERROR: this.props.managementProps.resources.wineClubManagement__errProcess,
        BLACKOUT_DATE: this.props.managementProps.resources.wineClubManagement__errBlackoutDate,
        MAX_SKIPPED: this.props.managementProps.resources.wineClubManagement__errMaxSkipped,
        INVALID_SHIPTOADDRESS: this.props.managementProps.resources.wineClubManagement__errInvalidShipToAddress,
        INVALID_CREDITCARD: this.props.managementProps.resources.wineClubManagement__errInvalidCreditCard
    };

    constructor(props: IWineClubMembershipProps) {
        super(props);

        this._editMembership = this._editMembership.bind(this);
        this._pauseMembership = this._pauseMembership.bind(this);
        this._resumeMembership = this._resumeMembership.bind(this);
        this._skipNextShipment = this._skipNextShipment.bind(this);
        this._toggleDropdown = this._toggleDropdown.bind(this);
        this._ajaxPauseMembership = this._ajaxPauseMembership.bind(this);
        this._ajaxResumeMembership = this._ajaxResumeMembership.bind(this);
        this._ajaxSkippingShipment = this._ajaxSkippingShipment.bind(this);
        this._ajaxEditClub = this._ajaxEditClub.bind(this);
    }

    public render(): JSX.Element | null {
        const { membership: { MemberStatus, WineClubName, WineClubDisplay, IsBlackoutPeriod, ActivationDate, NextClubRunDate } } = this.props;
        const enablePause = this.props.managementProps.config.enablePauseResume;
        // this is used to force remounting everytime. we dont want state to persist in
        // this case
        if (this._currentModalProps || this._editModalProps) {
            ++this._keyRefresher;
        }
        let inactiveClass = '';
        if (MemberStatus && this._clubStatusTable[MemberStatus] === 'Closed') {
            inactiveClass = 'wine-club-management__table-cell__paused';
        }
        let actions: JSX.Element;
        if (!MemberStatus || IsBlackoutPeriod || this._clubStatusTable[MemberStatus] !== 'Active') {
            actions = <div className='wine-club-management__blackout-period'>N/A</div>;
        } else if(this._clubStatusTable[MemberStatus] !== 'Closed' && enablePause) {
            actions = <div className='wine-club-management__blackout-period'>N/A</div>;
        } else {
            actions = (
                <>
                    {this._editAction}
                    {enablePause && this._pauseOrResume}
                    {/* {CanSkip && <a className='wine-club-management__action wine-club-management__action-skip' role='button' onClick={this._skipNextShipment}>{wineClubManagement__skip}</a>} */}
                </>
            );
        }
        return (
            <div className='wine-club-management__membership-line'>
                <div className='wine-club-management__table-row'>
                    {this._currentModal}
                    {
                        <div className='wine-club-management__table-cell wine-club-management__table-cell__status wine-club-management__status-active'>
                            {MemberStatus && this._clubStatusTable[MemberStatus]}
                        </div>
                    }
                    <div className='wine-club-management__table-cell wine-club-management__table-cell__club'>
                        {WineClubDisplay || WineClubName}
                    </div>
                    <div className='wine-club-management__table-cell wine-club-management__table-cell__date wine-club-management__table-cell__date-start'>
                        {this._formatDate(ActivationDate)}
                    </div>
                    <div className={`wine-club-management__table-cell wine-club-management__table-cell__date wine-club-management__table-cell__date-next ${inactiveClass}`}>
                        {this._formatDate(NextClubRunDate)}
                    </div>
                    <div className='wine-club-management__table-cell wine-club-management__table-cell__actions'>
                        {actions}
                        {this._currentModalProps && <WineClubAction key={`action${this._keyRefresher}`} {...this._currentModalProps} />}
                    </div>
                </div>
                <p className='wine-club-management__message-success'>{this._successMessage}</p>
                {this._editModalProps && <WineClubEditForm key={`form${this._keyRefresher}`}  {...this._editModalProps} />}
            </div>
        );
    }

    private _formatDate(rawDate: string | undefined): string {
        if (rawDate) {
            const formatDate = moment(rawDate);
            return formatDate.format('MM/DD/YY');
        }
        return '';
    }

    private get _editAction(): JSX.Element | undefined {
        const { wineClubManagement__edit } = this.props.managementProps.resources;
        const { MemberStatus } = this.props.membership;
        const editingClass = this.props.isEditingClub ? 'wine-club-management__action-disabled' : '';
        if (MemberStatus && this._clubStatusTable[MemberStatus] === 'Active') {
            return (
                <a
                    className={`wine-club-management__action wine-club-management__action-edit ${editingClass}`}
                    role='button'
                    onClick={!this.props.isEditingClub ? this._editMembership : undefined}
                >
                    {wineClubManagement__edit}
                </a>
            );
        }
        return undefined;
    }

    private get _pauseOrResume(): JSX.Element {
        const { wineClubManagement__pause, wineClubManagement__resume } = this.props.managementProps.resources;
        const { MemberStatus } = this.props.membership;
        const editingClass = this.props.isEditingClub ? 'wine-club-management__action-disabled' : '';
        if (MemberStatus && this._clubStatusTable[MemberStatus] === 'Active') {
            return (
                <a className={`wine-club-management__action wine-club-management__action-pause ${editingClass}`} role='button' onClick={!this.props.isEditingClub ? this._pauseMembership : undefined}>{wineClubManagement__pause}</a>
            );
        }
        return (
            <a className={`wine-club-management__action wine-club-management__action-resume ${editingClass}`} role='button' onClick={!this.props.isEditingClub ? this._resumeMembership : undefined}>{wineClubManagement__resume}</a>
        );
    }

    private _editMembership(): void {
        this.props.updateEditingClub(true);
        this._editModalProps = {
            title: this.props.managementProps.resources.wineClubManagement__editTitle,
            context: this.props.context,
            managementProps: this.props.managementProps,
            membership: this.props.membership,
            onDestroy: () => {
                this.props.updateEditingClub(false);
                this._editModalProps = undefined;
            },
            action: this._ajaxEditClub,
            actionDeniedLabel: this.props.managementProps.resources.wineClubManagement__cancel
        };
    }
    private _pauseMembership(): void {
        const { pauseMembershipText, pauseMembershipWarning } = this.props.managementProps.config;
        const {
            wineClubManagement__pauseTitle,
            wineClubManagement__pause,
            wineClubManagement__cancel
        } = this.props.managementProps.resources;
        const { WineClubName, WineClubDisplay } = this.props.membership;

        this._currentModalProps = {
            modalClass: 'wine-club-management__modal wine-club-management__modal-pause',
            subTitle: wineClubManagement__pauseTitle,
            title: WineClubDisplay || WineClubName || '',
            description: pauseMembershipText,
            warning: pauseMembershipWarning,
            actionConfirmedLabel: wineClubManagement__pause,
            actionDeniedLabel: wineClubManagement__cancel,
            action: this._ajaxPauseMembership,
            onDestroy: () => { this._currentModalProps = undefined; }
        };
    }

    private _resumeMembership(): void {
        const { resumeMembershipText, resumeMembershipWarning } = this.props.managementProps.config;
        const {
            wineClubManagement__resumeTitle,
            wineClubManagement__resume,
            wineClubManagement__cancel
        } = this.props.managementProps.resources;
        const { WineClubName, WineClubDisplay } = this.props.membership;

        this._currentModalProps = {
            modalClass: 'wine-club-management__modal wine-club-management__modal-resume',
            subTitle: wineClubManagement__resumeTitle,
            title: WineClubDisplay || WineClubName || '',
            description: resumeMembershipText,
            warning: resumeMembershipWarning,
            actionConfirmedLabel: wineClubManagement__resume,
            actionDeniedLabel: wineClubManagement__cancel,
            action: this._ajaxResumeMembership,
            onDestroy: () => { this._currentModalProps = undefined; }
        };
    }

    private _skipNextShipment(): void {
        const { skipShipmentText, skipShipmentWarning } = this.props.managementProps.config;
        const {
            wineClubManagement__skipTitle,
            wineClubManagement__skip,
            wineClubManagement__cancel
        } = this.props.managementProps.resources;
        const { WineClubName, WineClubDisplay } = this.props.membership;

        this._currentModalProps = {
            modalClass: 'wine-club-management__modal wine-club-management__modal-skip',
            subTitle: wineClubManagement__skipTitle,
            title: WineClubDisplay || WineClubName || '',
            description: skipShipmentText,
            warning: skipShipmentWarning,
            actionConfirmedLabel: wineClubManagement__skip,
            actionDeniedLabel: wineClubManagement__cancel,
            action: this._ajaxSkippingShipment,
            onDestroy: () => { this._currentModalProps = undefined; }
        };
    }

    private _toggleDropdown(): void {
        this._dropdownToggle = !this._dropdownToggle;
    }

    private _clearMessage(): void {
        setTimeout(() => {
            this._successMessage = '';
        }, 3000);
    }

    private async _ajaxPauseMembership(): Promise<void | string> {
        const response = await this._createMembershipStateChange(true);

        // catch for 400's, 500's etc
        if (!response?.Status) { return this._errorCodeMap.PROCESS_ERROR; }
        const errorResponse = this._evaulateResponses(response.Status);
        if (errorResponse) {
            return this._evaulateResponses(response.Status);
        }
        this.bus!.publish('success');
        this._successMessage = this.props.managementProps.config.success?.pause;
        this._clearMessage();
    }
    private async _ajaxResumeMembership(): Promise<void | string> {
        const response = await this._createMembershipStateChange(false);

        // catch for 400's, 500's etc
        if (!response?.Status) { return this._errorCodeMap.PROCESS_ERROR; }
        const errorResponse = this._evaulateResponses(response.Status);
        if (errorResponse) {
            return this._evaulateResponses(response.Status);
        }
        this.bus!.publish('success');
        this._successMessage = this.props.managementProps.config.success?.resume;
        this._clearMessage();
    }

    private async _ajaxSkippingShipment(): Promise<void | string> {
        const input = createWineShipmentStateInput(this.props.context.actionContext, { wineClubId: this.props.membership.WineClubId!, skipShipment: true });
        const response = await wineShipmentStateAction(input, this.props.context.actionContext);

        // catch for 400's, 500's etc
        if (!response?.Status) { return this._errorCodeMap.PROCESS_ERROR; }

        const errorResponse = this._evaulateResponses(response.Status);
        if (errorResponse) {
            return this._evaulateResponses(response.Status);
        }
        this.bus!.publish('success');
        this._successMessage = this.props.managementProps.config.success?.skip;
        this._clearMessage();
    }

    private async _ajaxEditClub(formData: wineClubEditFormData): Promise<void | string> {

        const membershipEdit = createEditMembershipInput(
            this.props.context.actionContext,
            {
                CreditCard: +formData.creditCard,
                WineClubId: this.props.membership.WineClubId!,
                ShipToAddress: +formData.shippingAddress,
                DeliveryPreference: formData.deliveryPreference,
                PickupLocationId: formData.pickupLocation
            }
        );
        let editResponse: IMembershipEditMembershipInput | undefined;
        try {
            editResponse = await getEditMembershipAction(membershipEdit, this.props.context.actionContext);
        } catch (e) {
            console.error(e);
        }

        let errorResponse = '';
        // catch for 400's, 500's etc
        if (!editResponse?.Status) {
            errorResponse += this._errorCodeMap.PROCESS_ERROR;
        } else {
            errorResponse += this._evaulateResponses(editResponse.Status) || '';
        }

        if (errorResponse.length > 0) { return errorResponse; }
        this.bus!.publish('success');
        this._successMessage = this.props.managementProps.config.success?.edit;
        this._clearMessage();
    }

    private _evaulateResponses(response: string): string | void {
        this.bus!.publish('error');
        if (this._errorCodeMap[response]) {
            return this._errorCodeMap[response];
        }
    }

    private _createMembershipStateChange(pauseResume: boolean): AsyncResult<IMembershipStateInput> {
        const input = createMembershipStateInput(this.props.context.actionContext, { pauseResume: pauseResume, wineClubId: this.props.membership.WineClubId! });
        return membershipStateAction(input, this.props.context.actionContext);
    }
}